import type { ReactNode } from 'react';
import { BRAND_TAGLINE, FOOTER_COMPACT_LINKS, FOOTER_SECTIONS } from '@/lib/constants';
import { NavAnchor } from '@/components/common/NavAnchor';

interface FooterProps {
  /**
   * `full`: four link columns (homepage, place inside the page container). `compact`: one row
   * (inner pages). `workspace`: the Terminal / Markets & Pricing footer (unclassed markup styled
   * by the page's scoped stylesheet, e.g. terminal.css `footer`).
   */
  variant?: 'full' | 'compact' | 'workspace';
  /** Optional live-region note shown under the full footer. */
  status?: ReactNode;
  /** Compact only: false drops "©" (canideal.org story pages read "CanIDeal™ …"). */
  copyright?: boolean;
  /** Workspace only: commercial disclosure line (target of "Commercial Disclosures" links). */
  disclosure?: ReactNode;
}

/** Shared site footer — the approved canideal.org footers. */
export function Footer({ variant = 'compact', status, copyright = true, disclosure }: FooterProps) {
  const year = new Date().getFullYear();

  if (variant === 'workspace') {
    return (
      <footer>
        <p>
          © {year} CanIDeal™ <span>{BRAND_TAGLINE}</span>
        </p>
        <div>
          {FOOTER_COMPACT_LINKS.map((link) => (
            <NavAnchor key={link.label} link={link} />
          ))}
        </div>
        {disclosure && (
          <p className="disclosure" id="commercial-disclosures">
            {disclosure}
          </p>
        )}
      </footer>
    );
  }

  if (variant === 'compact') {
    return (
      <footer className="cid-footer cid-footer--compact">
        <div className="cid-container">
          <div className="cid-footer__row">
            <p>
              {copyright ? `© ${year} CanIDeal™` : 'CanIDeal™'} <span>{BRAND_TAGLINE}</span>
            </p>
            <nav aria-label="Footer">
              {FOOTER_COMPACT_LINKS.map((link) => (
                <NavAnchor key={link.label} link={link} />
              ))}
            </nav>
          </div>
        </div>
      </footer>
    );
  }

  // Approved homepage.html footer; rendered inside the homepage container.
  return (
    <footer className="cid-footer cid-footer--full">
      <div className="cid-footer-grid">
        {FOOTER_SECTIONS.map((section) => (
          <div key={section.title}>
            <h3>{section.title}</h3>
            {section.links.map((link) => (
              <NavAnchor key={link.label} link={link} dialog />
            ))}
          </div>
        ))}
      </div>
      <small>© {year} CanIDeal. All rights reserved.</small>
      {status && (
        <p className="cid-footer-status" aria-live="polite">
          {status}
        </p>
      )}
    </footer>
  );
}
